import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plug } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { cn } from '@/lib/utils';

import { AdConnections, AdProvider, useAdsApi } from '../../../common/hooks/use-ads-api.hook';

export { useInsightsRange as useAdsRange, BarList } from '../assistant/assistant-shared';

/**
 * Money in the account's own currency.
 *
 * Never converted. A vendor with a euro Meta account and a dirham Google
 * account sees each in its own currency, because a conversion rate we chose
 * would be a number they cannot find on either invoice.
 */
export const formatMoney = (amount: number | null | undefined, currency?: string | null, digits = 0) => {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
    try {
        if (currency) {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency,
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }).format(amount);
        }
    } catch {
        // An unknown currency code falls through to a plain number.
    }
    return amount.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatNumber = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : value.toLocaleString();

/** Already a percentage from the API (4 means 4%), unlike the Shopping Assistant's shares. */
export const formatPct = (value: number | null | undefined) => (value === null || value === undefined ? '—' : `${value}%`);

export const formatRoas = (value: number | null | undefined) => (value === null || value === undefined ? '—' : `${value}×`);

/** Change against the previous period, or null when there was nothing to compare with. */
export const changeOf = (current: number | null | undefined, previous: number | null | undefined): number | null => {
    if (current === null || current === undefined || !previous) return null;
    return Math.round(((current - previous) / previous) * 1000) / 10;
};

/**
 * Which platform a row belongs to.
 *
 * A letter mark rather than the brands' logos: this sits in a dense table on
 * every row, and at that size a logo is a coloured smudge while a letter in a
 * neutral tile reads instantly and stays monochrome like the rest of the page.
 */
export const ProviderMark = ({ provider, withName = false }: { provider: AdProvider | string | null; withName?: boolean }) => {
    const name = provider === 'google' ? 'Google' : provider === 'meta' ? 'Meta' : '—';
    return (
        <span className="inline-flex items-center gap-2">
            <span
                aria-hidden
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-medium"
            >
                {provider === 'google' ? 'G' : provider === 'meta' ? 'M' : '·'}
            </span>
            {withName ? <span className="text-sm">{name}</span> : <span className="sr-only">{name}</span>}
        </span>
    );
};

export const CampaignStatusBadge = ({ status }: { status: string }) => {
    const { t } = useTranslation();
    const labels: Record<string, string> = {
        active: t('ADS.STATUS.ACTIVE', 'Active'),
        paused: t('ADS.STATUS.PAUSED', 'Paused'),
        ended: t('ADS.STATUS.ENDED', 'Ended'),
        draft: t('ADS.STATUS.DRAFT', 'Draft'),
    };
    return (
        <span className="inline-flex items-center gap-1.5 text-sm">
            <span
                aria-hidden
                className={cn('size-1.5 rounded-full', status === 'active' ? 'bg-success' : 'bg-muted-foreground/50')}
            />
            {labels[status] ?? status}
        </span>
    );
};

export const ConnectionStatusBadge = ({ status }: { status: string }) => {
    const { t } = useTranslation();
    if (status === 'needs_reauth') {
        return <Badge variant="destructive">{t('ADS.CONNECTIONS.NEEDS_REAUTH', 'Reconnect needed')}</Badge>;
    }
    if (status === 'connected') {
        return <Badge variant="secondary">{t('ADS.CONNECTIONS.CONNECTED', 'Connected')}</Badge>;
    }
    return <Badge variant="outline">{t('ADS.CONNECTIONS.DISCONNECTED', 'Disconnected')}</Badge>;
};

/**
 * What the API said went wrong, in its own words.
 *
 * The request helper throws the raw Response on a 4xx, so the body has to be
 * read here. The services write their refusals as sentences for vendors —
 * "Meta needs at least 100 people" — and dropping them for a generic
 * "Something went wrong" throws away the one useful part.
 */
export async function readApiError(error: unknown, fallback: string): Promise<string> {
    try {
        if (error && typeof (error as Response).json === 'function') {
            const body: any = await (error as Response).clone().json();
            const message = Array.isArray(body?.message) ? body.message.join(' ') : body?.message;
            if (message) return String(message);
        }
    } catch {
        // Not JSON; fall back.
    }
    return (error as Error)?.message || fallback;
}

/** The vendor's connections, shared by every page that has to decide whether to show numbers at all. */
export const useAdConnections = () => {
    const api = useAdsApi();
    const [data, setData] = useState<AdConnections | null>(null);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        setLoading(true);
        try {
            setData(await api.getConnections());
        } catch (error) {
            console.error('Error loading ad connections:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { data, loading, reload, hasConnection: !!data?.connections.some((c) => c.status !== 'disconnected') };
};

/** Every Ads page before an account is connected: one clear next step, not an empty dashboard of zeros. */
export const ConnectFirst = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    return (
        <Empty className="border">
            <EmptyHeader>
                <EmptyMedia variant="icon"><Plug /></EmptyMedia>
                <EmptyTitle>{t('ADS.EMPTY.TITLE', 'Connect your ad accounts')}</EmptyTitle>
                <EmptyDescription>
                    {t(
                        'ADS.EMPTY.DESCRIPTION',
                        'Connect Meta or Google Ads to see what your spending brings to your site — people who tried your products in their own rooms and left their details — and what to change.',
                    )}
                </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
                <Button variant="outline" onClick={() => navigate('/ads/connections')}>
                    <Plug data-icon="inline-start" />
                    {t('ADS.EMPTY.CTA', 'Connect an account')}
                </Button>
            </EmptyContent>
        </Empty>
    );
};

/** Page heading with an optional action slot, as the other sections lay it out. */
export const PageHeading = ({ title, subtitle, children }: { title: string; subtitle: string; children?: React.ReactNode }) => (
    <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">{title}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
);
